import Adaptr from '@adaptr/web-sdk';
import Player from '@adaptr/web-sdk/dist/player';
import { AudioFile } from '@adaptr/web-sdk/dist/types';
import { Base64 } from 'js-base64';

import Observable from './Observable';
import { IMedia } from './Media';

type OptionsType = {
  token?: string,
  secret?: string,
} | {
  requestParamName: string,
  requestParamValue: string,
};

class AdaptrFeedFm extends Observable implements IMedia {
  static EVENT = {
    END: 'ended',
    LOAD: 'load',
    LOAD_ERROR: 'error',
  };

  private readonly id: string;

  private readonly audioId: number;

  private readonly audioName: string;

  private token?: string;

  private secret?: string;

  private requestParamsQueryString?: string;

  // private log: HTMLDivElement;

  private player: Player | null = null;

  private audioFile: AudioFile | null = null;

  private options = {
    isLooping: false,
    isMuted: false,
    volume: 1,
  };

  constructor(id: string, audioId: number, audioName: string, options: OptionsType) {
    super();
    this.id = id;
    this.audioId = audioId;
    this.audioName = audioName;
    if ('token' in options) {
      this.token = options?.token || undefined;
      this.secret = options?.secret || undefined;
    }
    if ('requestParamName' in options) {
      this.requestParamsQueryString = `${options.requestParamName}=${options.requestParamValue}`;
    }
    // this.log = document.createElement('div');
    // this.log.style.position = 'fixed';
    // this.log.style.top = '0';
    // this.log.style.right = '0';
    // this.log.style.background = 'rgba(0,0,0,0.5)';
    // this.log.style.color = '#ffffff';
    // this.log.style.maxHeight = '500px';
    // this.log.style.pointerEvents = 'none';
    // this.log.style.zIndex = '10000';
    // document.body.appendChild(this.log);
  }

  private handlePlayEnd = () => {
    if (this.options.isLooping) {
      this.play();
    }
    this.dispatchEvent(AdaptrFeedFm.EVENT.END, {});
  };

  public volume(value: number) {
    // this.log.innerHTML = `${this.log.innerHTML}<br />volume ${value}`;
    this.options.volume = value;
    this.player?.setVolume(value * 100);
    if (this.player?.getCurrentState() === 'playing') {
      this.player?.play();
    }
    return this;
  }

  public getVolume(): number {
    return this.options.volume;
  }

  public mute() {
    this.options.isMuted = true;
    this.player?.setMuted(true);
    return this;
  }

  public unMute() {
    this.options.isMuted = false;
    this.player?.setMuted(false);
    return this;
  }

  public isMute(): boolean {
    return this.options.isMuted;
  }

  public isPlaying(): boolean {
    return this.player?.getCurrentState() === 'playing';
  }

  public isPaused(): boolean {
    return this.player?.getCurrentState() === 'paused';
  }

  private prepareCredentials = async () => {
    if (this.token && this.secret) {
      return true;
    }
    let token = '';
    let anonymousIdentifier = '';
    if (typeof window !== 'undefined') {
      const searchParams = new URLSearchParams(window.location.search || '');
      const mode = searchParams.get('mode');
      const encodedSender = searchParams.get('sender');
      if (mode === 'preview' && encodedSender) {
        try {
          const sender = JSON.parse(Base64.decode(encodedSender));
          token = sender.key;
          anonymousIdentifier = sender.uuid;
        } catch (e) {
          token = '';
          anonymousIdentifier = '';
        }
      }
    }
    let queryString = '';
    const headers = new Headers({
      'Content-Type': 'application/json',
    });
    if (token) {
      headers.set('Authorization', `Token ${token}`);
    } else
    if (!token && anonymousIdentifier) {
      queryString = `?anonymous_identifier=${anonymousIdentifier}`;
    } else {
      queryString = `?${this.requestParamsQueryString}`;
    }
    return fetch(`${process.env.REACT_APP_REST_API_ADDRESS}/api/v1/feed_fm/access_token_new${queryString}`, { method: 'get', headers })
      .then((res) => res.json())
      .catch((error) => {
        console.error(error.message);
        return false;
      })
      .then((result: { recipient: { token: string, secret: string }, sender: { token: string, secret: string } }) => {
        const { sender, recipient } = result || {};
        this.token = (token ? sender : recipient)?.token || '';
        this.secret = (token ? sender : recipient)?.secret || '';
        return true;
      });
  };

  public load(): this {
    Promise.resolve().then(async () => {
      await this.prepareCredentials();
      if (!this.token || !this.secret) {
        this.dispatchEvent(AdaptrFeedFm.EVENT.LOAD_ERROR, {});
        return;
      }
      this.player = new Adaptr.Player(this.token, this.secret);
      this.player.on('play-ended', this.handlePlayEnd);

      const isInitialized = await this.player.initialize().then(() => true).catch((error) => {
        console.error(error.message);
        return false;
      });
      if (!isInitialized) {
        this.dispatchEvent(AdaptrFeedFm.EVENT.LOAD_ERROR, {});
        return;
      }

      const foundAudioFiles = await this.player?.searchAudioFiles(this.audioName);
      if (!foundAudioFiles || foundAudioFiles.total === 0) {
        this.dispatchEvent(AdaptrFeedFm.EVENT.LOAD_ERROR, {});
        return;
      }
      this.audioFile = foundAudioFiles.results.find((item) => Number(item.audioFile.id) === Number(this.audioId))?.audioFile || null;
      if (!this.audioFile) {
        this.dispatchEvent(AdaptrFeedFm.EVENT.LOAD_ERROR, {});
        return;
      }

      // await this.player.preparePreview(this.audioFile);
      this.dispatchEvent(AdaptrFeedFm.EVENT.LOAD, {});
    });
    return this;
  }

  public loop(value: boolean) {
    this.options.isLooping = value;
    return this;
  }

  public isLooping(): boolean {
    return this.options.isLooping;
  }

  public pause() {
    // this.log.innerHTML = `${this.log.innerHTML}<br />pause`;
    this.player?.pause();
    return this;
  }

  public play() {
    // this.log.innerHTML = `${this.log.innerHTML}<br />play`;
    if (this.isPlaying()) {
      return this;
    }
    if (this.isPaused()) {
      this.player?.play();
      return this;
    }
    if (!this.audioFile) {
      return this;
    }
    this.player?.playPreview(this.audioFile).then(() => {
      this.player?.setMuted(this.options.isMuted);
      this.volume(this.options.volume);
    }).catch((error) => {
      console.error(error.message);
    });
    return this;
  }

  public stop() {
    this.player?.stop();
    return this;
  }

  public destroy(): void {
    this.player?.stop();
    this.player?.off('play-ended', this.handlePlayEnd);
  }
}

export default AdaptrFeedFm;
